import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Typography, Alert, FormLabel, OutlinedInput, Grid2, MenuItem, Select, FormControl, FormControlLabel, Checkbox, IconButton, ClickAwayListener, Popper, Paper } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import createAnnouncementService from '../../utils/AnnouncementService';
import createStoreService from '../../utils/StoreService';
import { Dayjs } from 'dayjs';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import EmojiPicker from 'emoji-picker-react';
import { useNavigate } from 'react-router-dom';
import { ChevronRightRounded } from '@mui/icons-material';

const CreateAnnouncement = () => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [executionDate, setExecutionDate] = useState<Dayjs | null>(null);
  const [storeIds, setStoreIds] = useState<string[]>([]);
  const [stores, setStores] = useState<{ id: string; name: string }[]>([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isOnlyForPreviousCustomers, setIsOnlyForPreviousCustomers] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const descriptionInputRef = useRef<HTMLTextAreaElement>(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const { getMyStoresAsync } = createStoreService();
        const response = await getMyStoresAsync();
        if (response.status === 200) {
          setStores(response.data.map((store: any) => ({ id: store.id, name: store.name })));
        }
      } catch (error) {
        console.error('Failed to fetch stores:', error);
      }
    };

    fetchStores();
  }, []);

  const handleSubmit = async () => {
    if (!title || !description || !executionDate || storeIds.length === 0) {
      setErrorMessage('All fields are required, and at least one store must be selected.');
      setSuccessMessage('');
      return;
    }

    try {
      const announcementData = { title, description: description, startDate: executionDate, storeIds, isOnlyForPreviousCustomers };
      const { createAnnouncement } = createAnnouncementService();
      await createAnnouncement(announcementData);
      setSuccessMessage('Announcement created successfully!');
      setErrorMessage('');
      setTitle('');
      setDescription('');
      setExecutionDate(null);
      setStoreIds([]);
      setIsOnlyForPreviousCustomers(false);
    } catch (error) {
      setErrorMessage('Failed to create announcement.');
      setSuccessMessage('');
    }
  };
  return (
    <Box sx={{ maxWidth: 600, margin: 'auto', mt: 4 }}>
      <Grid2 sx={{display: 'flex', justifyContent: 'space-between'}} >
        <Typography variant="h4" gutterBottom>
          Create Announcement
        </Typography>
        <Button
          variant="outlined"
          onClick={() => navigate('/')}
          endIcon={<ChevronRightRounded />}
        >
          Go to Dashboard
        </Button>
      </Grid2>
      {successMessage && (
        <Box display="flex" alignItems="center" mb={2}>
          <CheckCircleOutlineIcon color="success" fontSize="large" sx={{ mr: 1 }} />
          <Typography color="success.main">{successMessage}</Typography>
        </Box>
      )}
      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
      <Grid2 container spacing={3}>
        <Grid2 size={{ xs: 12 }}>
          <FormLabel htmlFor="title" required>
            Title
          </FormLabel>
          <OutlinedInput
            id="title"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Grid2>        <Grid2 size={{ xs: 12 }}>
          <FormLabel htmlFor="description" required>
            Content & Description
          </FormLabel>
          <Box sx={{ position: 'relative' }}>
            <OutlinedInput
              id="description"
              fullWidth
              multiline
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              inputRef={descriptionInputRef}
              endAdornment={
                <IconButton 
                  ref={emojiButtonRef}
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  sx={{ position: 'absolute', bottom: 8, right: 8 }}
                  size="small"
                >
                  <SentimentSatisfiedAltIcon />
                </IconButton>
              }
            />
            <Popper
              open={showEmojiPicker}
              anchorEl={emojiButtonRef.current}
              placement="top-end"
              style={{ zIndex: 1300 }}
            >
              <ClickAwayListener onClickAway={() => setShowEmojiPicker(false)}>
                <Paper elevation={3}>
                  <EmojiPicker
                    onEmojiClick={(emojiObject) => {
                      setDescription(prevDescription => prevDescription + emojiObject.emoji);
                      if (descriptionInputRef.current) {
                        descriptionInputRef.current.focus();
                      }
                    }}
                    width={300}
                    height={400}
                  />
                </Paper>
              </ClickAwayListener>
            </Popper>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12 }}>
          <FormLabel htmlFor="execution-date" required>
            Execution Date & Time
          </FormLabel>
          <DateTimePicker
            value={executionDate}
            onChange={(newValue) => setExecutionDate(newValue)}
            slotProps={{ textField: { fullWidth: true } }}
            format="DD/MM/YYYY HH:mm"
          />
        </Grid2>
        <Grid2 size={{ xs: 12 }}>
          <FormLabel htmlFor="store-ids" required>
            Select Stores
          </FormLabel>
          <FormControl fullWidth>
            <Select
              id="store-ids"
              multiple
              value={storeIds}
              onChange={(e) => setStoreIds(e.target.value as string[])}
              renderValue={(selected) => selected.map((id) => stores.find((store) => store.id === id)?.name).join(', ')}
            >
              {stores.map((store) => (
                <MenuItem key={store.id} value={store.id}>
                  {storeIds.includes(store.id) && <CheckIcon fontSize="small" style={{ marginRight: 8 }} />}
                  {store.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid2>
        <Grid2 size={{ xs: 12 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isOnlyForPreviousCustomers}
                onChange={(e) => setIsOnlyForPreviousCustomers(e.target.checked)}
                name="isOnlyForPreviousCustomers"
                color="primary"
              />
            }
            label="Previous Customers Only"
          />
        </Grid2>
        <Grid2 size={{ xs: 12 }} sx={{ justifyContent: 'end', display: 'flex' }}>
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            Submit
          </Button>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default CreateAnnouncement;
